import React, { useCallback, useEffect, useMemo, useState } from "react";
import ExcelJS from "exceljs";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaArrowLeft,
  FaBuilding,
  FaCalendarAlt,
  FaCaretDown,
  FaCaretUp,
  FaChartBar,
  FaDownload,
  FaDollarSign,
  FaBan,
  FaFileInvoiceDollar,
  FaInbox,
  FaList,
  FaSearch,
  FaSort,
  FaStream,
  FaSync,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EnvToggle } from "../OrgUsageAnalytics/components/EnvToggle";
import { KpiCard } from "../OrgUsageAnalytics/components/KpiCard";
import { useEnv } from "../OrgUsageAnalytics/useEnv";
import "../OrgUsageAnalytics/OrgUsageAnalytics.css";
import "./EnterpriseBillingUsage.css";
import {
  fetchBillingUsageDetail,
  fetchBillingUsageOverview,
  setLoanBillingOverride,
} from "./api";

// Client-app origins per env. Matches the constants other admin pages already
// use (see OrganizationLimits, LangfuseAnalytics) so deep-links route to the
// right deployment instead of the admin dashboard's own host (which has no
// /loans route).
const ADDY_APP_URL_PROD = "https://app.addy.so";
const ADDY_APP_URL_DEV = "https://devmail.addy.so";

const MONTH_OPTIONS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const getCurrentPeriod = () => {
  const now = new Date();
  return {
    year: String(now.getFullYear()),
    month: String(now.getMonth() + 1).padStart(2, "0"),
  };
};

const fmtN = (value) => Number(value || 0).toLocaleString();

const fmtMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const monthLabel = (monthKey) => {
  if (!monthKey) return "";
  if (monthKey === "all-time") return "All time";
  if (!monthKey.includes("-")) return monthKey;
  const [year, month] = monthKey.split("-");
  const monthName = MONTH_OPTIONS.find((m) => m.value === month)?.label || month;
  return `${monthName} ${year}`;
};

const ALL_TIME_VALUE = "all-time";

function useBillingPeriod({ allowAllTime = false } = {}) {
  const current = getCurrentPeriod();
  const [year, setYear] = useState(current.year);
  const [month, setMonth] = useState(current.month);
  const currentYear = Number(current.year);

  const yearOptions = useMemo(() => {
    const years = [];
    if (allowAllTime) years.push(ALL_TIME_VALUE);
    for (let y = 2024; y <= currentYear; y++) years.push(String(y));
    return years;
  }, [allowAllTime, currentYear]);

  const isAllTime = year === ALL_TIME_VALUE;

  const availableMonths = useMemo(() => {
    if (isAllTime) return [];
    if (year === current.year) {
      return MONTH_OPTIONS.filter((m) => Number(m.value) <= Number(current.month));
    }
    return MONTH_OPTIONS;
  }, [current.month, current.year, isAllTime, year]);

  useEffect(() => {
    if (isAllTime) return;
    if (!availableMonths.some((m) => m.value === month)) {
      setMonth(availableMonths[availableMonths.length - 1]?.value || "01");
    }
  }, [availableMonths, isAllTime, month]);

  return {
    year,
    setYear,
    month,
    setMonth,
    isAllTime,
    // Empty string here means "no monthAndYear param" so the backend resolves
    // to all-time on endpoints that support it.
    monthAndYear: isAllTime ? "" : `${year}-${month}`,
    yearOptions,
    availableMonths,
  };
}

function PeriodSelects({ period }) {
  const yearLabel = (year) => (year === ALL_TIME_VALUE ? "All time" : year);
  return (
    <div className="ebu-period">
      <FaCalendarAlt style={{ color: "var(--ou-dim)", fontSize: 12 }} />
      <select
        className="ebu-select"
        value={period.year}
        onChange={(e) => period.setYear(e.target.value)}
      >
        {period.yearOptions.map((year) => (
          <option value={year} key={year}>
            {yearLabel(year)}
          </option>
        ))}
      </select>
      <select
        className="ebu-select"
        value={period.month}
        onChange={(e) => period.setMonth(e.target.value)}
        disabled={period.isAllTime}
      >
        {period.isAllTime ? (
          <option value="">—</option>
        ) : (
          period.availableMonths.map((month) => (
            <option value={month.value} key={month.value}>
              {month.label}
            </option>
          ))
        )}
      </select>
    </div>
  );
}

function escapeCsv(value) {
  const str = String(value ?? "");
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function downloadCsv(filename, headers, rows) {
  // `headers` is optional: a grouped/sectioned export (buildGroupedRows) already
  // carries its own per-section headers, so pass null to skip a global header.
  const lines = (headers ? [headers, ...rows] : rows).map((row) =>
    row.map(escapeCsv).join(","),
  );
  const csv = lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// Bounded-concurrency map used by the flat export so expanding many orgs into
// their loans doesn't fire dozens of heavy detail requests at once (each one
// is a full Firestore loan scan on the backend).
async function mapWithConcurrency(items, limit, mapper) {
  const results = new Array(items.length);
  let next = 0;
  const workerCount = Math.min(limit, items.length);
  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (next < items.length) {
        const index = next++;
        results[index] = await mapper(items[index], index);
      }
    }),
  );
  return results;
}

// Per-loan table header, printed once under each organization section. Mirrors
// LOAN_TABLE_HEADERS in backend services/billingUsageExport.js so the two
// exports stay identical.
const LOAN_TABLE_HEADERS = [
  "Loan ID",
  "Borrower",
  "Stage",
  "Funded Date",
  "Checklist Completed",
  "Billable",
  "Rate Applied",
  "Charge",
];

function buildLoanCells(loan) {
  return [
    loan.id,
    loan.borrowerName || "",
    loan.stageName || "",
    loan.fundedDate || "",
    loan.checklistCompleted ? "yes" : "no",
    loan.isBillable ? "yes" : "no",
    loan.rateApplied || 0,
    loan.charge || 0,
  ];
}

// ---------------------------------------------------------------------------
// Styled workbook (.xlsx) — mirrors backend services/billingUsageExport.js so
// the on-demand admin download and the automated monthly file look identical.
// ---------------------------------------------------------------------------

const TABLE_COLS = LOAN_TABLE_HEADERS.length; // 8
const MONEY_FMT = '"$"#,##0.00';

const XLSX_COLOR = {
  headerBg: "FF1F2937",
  headerText: "FFFFFFFF",
  summaryBg: "FFF3F4F6",
  tableHeadBg: "FFE5E7EB",
  subtotalBg: "FFF9FAFB",
  gridLine: "FFD1D5DB",
  muted: "FF6B7280",
  text: "FF111827",
};

const THIN_BORDER = {
  top: { style: "thin", color: { argb: XLSX_COLOR.gridLine } },
  left: { style: "thin", color: { argb: XLSX_COLOR.gridLine } },
  bottom: { style: "thin", color: { argb: XLSX_COLOR.gridLine } },
  right: { style: "thin", color: { argb: XLSX_COLOR.gridLine } },
};

const solidFill = (argb) => ({
  type: "pattern",
  pattern: "solid",
  fgColor: { argb },
});

function borderAcross(row) {
  for (let c = 1; c <= TABLE_COLS; c += 1) {
    row.getCell(c).border = THIN_BORDER;
  }
}

// Render one org "section": bold header, one-line summary, bordered per-loan
// table, and a bold subtotal. Org-level price/summary appears once; each loan
// carries its own rate/charge.
function addOrgSection(ws, org, loans) {
  const title = ws.addRow([`${org.organizationName}  ·  ${org.organizationId}`]);
  ws.mergeCells(title.number, 1, title.number, TABLE_COLS);
  title.height = 22;
  title.getCell(1).font = {
    bold: true,
    size: 13,
    color: { argb: XLSX_COLOR.headerText },
  };
  title.getCell(1).fill = solidFill(XLSX_COLOR.headerBg);
  title.getCell(1).alignment = { vertical: "middle", indent: 1 };

  const price = org.pricePerLoan == null ? "n/a" : fmtMoney(org.pricePerLoan);
  const summary = ws.addRow([
    `Price/loan: ${price}      Billable: ${fmtN(org.billableLoans ?? 0)}      ` +
      `Funded: ${fmtN(org.totalLoans ?? 0)}      Non-billable: ${fmtN(org.nonBillableLoans ?? 0)}      ` +
      `Org charge: ${fmtMoney(org.totalCharge ?? 0)}`,
  ]);
  ws.mergeCells(summary.number, 1, summary.number, TABLE_COLS);
  summary.height = 18;
  summary.getCell(1).font = { size: 11, color: { argb: XLSX_COLOR.text } };
  summary.getCell(1).fill = solidFill(XLSX_COLOR.summaryBg);
  summary.getCell(1).alignment = { vertical: "middle", indent: 1 };

  const head = ws.addRow(LOAN_TABLE_HEADERS);
  borderAcross(head);
  head.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: XLSX_COLOR.text } };
    cell.fill = solidFill(XLSX_COLOR.tableHeadBg);
    cell.alignment = { vertical: "middle" };
  });

  if (loans.length) {
    loans.forEach((loan) => {
      const row = ws.addRow(buildLoanCells(loan));
      borderAcross(row);
      row.getCell(7).numFmt = MONEY_FMT;
      row.getCell(8).numFmt = MONEY_FMT;
    });
  } else {
    const empty = ws.addRow(["(no funded loans this month)"]);
    ws.mergeCells(empty.number, 1, empty.number, TABLE_COLS);
    borderAcross(empty);
    empty.getCell(1).font = { italic: true, color: { argb: XLSX_COLOR.muted } };
    empty.getCell(1).alignment = { indent: 1 };
  }

  const subtotal = ws.addRow([]);
  ws.mergeCells(subtotal.number, 1, subtotal.number, TABLE_COLS - 1);
  subtotal.getCell(1).value = `Subtotal — ${fmtN(org.billableLoans ?? 0)} billable`;
  subtotal.getCell(TABLE_COLS).value = org.totalCharge ?? 0;
  borderAcross(subtotal);
  for (let c = 1; c <= TABLE_COLS; c += 1) {
    subtotal.getCell(c).fill = solidFill(XLSX_COLOR.subtotalBg);
  }
  subtotal.getCell(1).font = { bold: true, color: { argb: XLSX_COLOR.text } };
  subtotal.getCell(1).alignment = { horizontal: "right", vertical: "middle" };
  subtotal.getCell(TABLE_COLS).font = {
    bold: true,
    color: { argb: XLSX_COLOR.text },
  };
  subtotal.getCell(TABLE_COLS).numFmt = MONEY_FMT;

  ws.addRow([]); // blank separator between org sections
}

function addGrandTotal(ws, grand) {
  const row = ws.addRow([]);
  ws.mergeCells(row.number, 1, row.number, TABLE_COLS - 1);
  row.getCell(1).value = `TOTAL (all organizations) — ${fmtN(grand.billableLoans)} billable`;
  row.getCell(TABLE_COLS).value = grand.totalCharge;
  row.height = 22;
  for (let c = 1; c <= TABLE_COLS; c += 1) {
    row.getCell(c).fill = solidFill(XLSX_COLOR.headerBg);
    row.getCell(c).border = THIN_BORDER;
  }
  row.getCell(1).font = {
    bold: true,
    size: 12,
    color: { argb: XLSX_COLOR.headerText },
  };
  row.getCell(1).alignment = { horizontal: "right", vertical: "middle" };
  row.getCell(TABLE_COLS).font = {
    bold: true,
    size: 12,
    color: { argb: XLSX_COLOR.headerText },
  };
  row.getCell(TABLE_COLS).numFmt = MONEY_FMT;
}

// Build the styled billing workbook from org sections (each already paired with
// its funded loans) plus the grand total.
function buildBillingWorkbook(sections, grand) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Addy AI";
  workbook.created = new Date();

  const ws = workbook.addWorksheet("Billing Usage", {
    views: [{ state: "frozen", ySplit: 0 }],
  });
  ws.columns = [
    { width: 24 },
    { width: 26 },
    { width: 20 },
    { width: 14 },
    { width: 20 },
    { width: 10 },
    { width: 14 },
    { width: 14 },
  ];

  sections.forEach(({ org, loans }) => addOrgSection(ws, org, loans));
  addGrandTotal(ws, grand);

  return workbook;
}

// Write the workbook to a Blob and trigger a browser download.
async function downloadWorkbook(filename, workbook) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// Backend-error-message → user-facing badge mapping. The raw strings here
// match what `controllers/billingUsage.js` `validateBillingConfig` returns
// today. We treat **setup issues** (org never had pricing configured) as
// "warn" (amber) — they're data-hygiene work, not system failures — and
// only show "Error" (red) for actual calculation crashes. The raw message
// still appears in the tooltip so debugging is unimpaired.
function describeRowStatus(row) {
  if (row.status === "ok") {
    return { label: "Ready", variant: "ok" };
  }
  const raw = row.error || "";
  if (/billing not configured/i.test(raw)) {
    return { label: "No Pricing", variant: "warn" };
  }
  if (/pricePerLoan/i.test(raw)) {
    return { label: "Bad Rate", variant: "warn" };
  }
  if (/basePricePerLoan/i.test(raw)) {
    return { label: "Bad Base Rate", variant: "warn" };
  }
  if (/discountRate/i.test(raw)) {
    return { label: "Bad Discount", variant: "warn" };
  }
  // Fall-through is genuinely unexpected — calculator threw, Firestore
  // unavailable, etc. Show the alarming red so it stands out among the
  // (many) setup-issue rows.
  return { label: "Error", variant: "error" };
}

function StatusBadge({ row }) {
  const { label, variant } = describeRowStatus(row);
  return (
    <span
      className={`ebu-badge ebu-badge--${variant}`}
      title={row.error || ""}
    >
      {label}
    </span>
  );
}

function LoadingPanel({ label }) {
  return (
    <div className="ebu-panel">
      <div className="ebu-loading">
        <FaSync className="fa-spin" />
        <div style={{ marginTop: 10 }}>{label}</div>
      </div>
    </div>
  );
}

function EmptyPanel({ title, subtitle }) {
  return (
    <div className="ebu-panel">
      <div className="ebu-empty">
        <div className="ebu-empty-icon">
          <FaInbox />
        </div>
        <div className="ebu-empty-title">{title}</div>
        <div className="ebu-empty-sub">{subtitle}</div>
      </div>
    </div>
  );
}

// Each sortable column declares its default direction (the one that's
// applied when the user first clicks the header). Revenue and loan-count
// columns default to descending — a CFO opening the dashboard wants
// "biggest first" almost always. Name columns default to ascending (A-Z).
const SORT_COLUMNS = {
  name: { defaultDirection: "asc" },
  status: { defaultDirection: "asc" }, // "ok" sorts before "error" lexicographically
  projectedCost: { defaultDirection: "desc" },
};

const DEFAULT_SORT = { key: "projectedCost", direction: "desc" };

/**
 * Comparator factory for the overview org rows.
 *
 * Three invariants the CFO dashboard wants regardless of which column the
 * user picks:
 *   1. status === "ok" rows always sort BEFORE error rows (errors at the
 *      bottom). Sorting by revenue and seeing a $0 errored org at the top
 *      because "$0 is the lowest" would be misleading.
 *   2. The active sort column drives the within-group ordering.
 *   3. Organization name is the final tiebreaker so the order is stable
 *      across re-renders when two rows tie on the active column.
 */
function makeOrgComparator({ key, direction }) {
  const dir = direction === "asc" ? 1 : -1;
  return (a, b) => {
    // Invariant 1: ok-first, errors-last (always).
    const aOk = a.status === "ok" ? 0 : 1;
    const bOk = b.status === "ok" ? 0 : 1;
    if (aOk !== bOk) return aOk - bOk;

    // Invariant 2: the active sort column.
    let aVal;
    let bVal;
    if (key === "name") {
      aVal = (a.organizationName || "").toLowerCase();
      bVal = (b.organizationName || "").toLowerCase();
    } else if (key === "status") {
      aVal = a.status || "";
      bVal = b.status || "";
    } else if (key === "projectedCost") {
      aVal = a.totalCharge || 0;
      bVal = b.totalCharge || 0;
    } else {
      aVal = 0;
      bVal = 0;
    }
    if (aVal < bVal) return -1 * dir;
    if (aVal > bVal) return 1 * dir;

    // Invariant 3: stable secondary by org name.
    const aName = (a.organizationName || "").toLowerCase();
    const bName = (b.organizationName || "").toLowerCase();
    if (aName < bName) return -1;
    if (aName > bName) return 1;
    return 0;
  };
}

function SortableHeader({ columnKey, sort, setSort, children, align }) {
  const isActive = sort.key === columnKey;
  const handleClick = () => {
    setSort((prev) => {
      if (prev.key !== columnKey) {
        return {
          key: columnKey,
          direction: SORT_COLUMNS[columnKey].defaultDirection,
        };
      }
      // Same column → flip direction.
      return {
        key: columnKey,
        direction: prev.direction === "asc" ? "desc" : "asc",
      };
    });
  };
  const indicator = !isActive ? (
    <FaSort className="ebu-sort-icon" />
  ) : sort.direction === "asc" ? (
    <FaCaretUp className="ebu-sort-icon ebu-sort-icon--active" />
  ) : (
    <FaCaretDown className="ebu-sort-icon ebu-sort-icon--active" />
  );
  return (
    <th
      className={`ebu-th-sortable${isActive ? " is-active" : ""}${
        align === "right" ? " ebu-th-right" : ""
      }`}
      onClick={handleClick}
    >
      <span className="ebu-th-content">
        {children}
        {indicator}
      </span>
    </th>
  );
}

const EnterpriseBillingUsagePage = ({ onReady }) => {
  const history = useHistory();
  const [env, setEnv] = useEnv();
  const period = useBillingPeriod();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  // Default = projectedCost DESC + status=="ok" first, so paying customers
  // ordered by revenue are at the top. Errors/zero-rev fall to the bottom
  // regardless of sort direction (see makeOrgComparator).
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // `force` distinguishes the Refresh button (which must bypass both the
  // frontend module cache AND the backend in-memory cache) from the normal
  // auto-load that runs when env/period/search change (which is fine to
  // serve from cache if it's warm).
  const load = useCallback(
    async ({ force = false } = {}) => {
      setLoading(true);
      try {
        const json = await fetchBillingUsageOverview(
          {
            monthAndYear: period.monthAndYear,
            search: debouncedSearch,
          },
          env,
          { refresh: force },
        );
        setData(json);
      } catch (err) {
        toast.error(err.message || "Failed to load enterprise billing usage");
        setData(null);
      } finally {
        setLoading(false);
        onReady?.();
      }
    },
    [debouncedSearch, env, onReady, period.monthAndYear],
  );

  useEffect(() => {
    load();
  }, [load]);

  const totals = data?.totals || {};
  const orgs = data?.orgs || [];

  // Sort once per (orgs, sort) change. The CSV export uses the same order
  // so what's on screen is what's downloaded.
  const sortedOrgs = useMemo(
    () => [...orgs].sort(makeOrgComparator(sort)),
    [orgs, sort],
  );

  // Grouped Excel export: one bordered section per org (header + summary +
  // per-loan table + subtotal), then a grand total. Only REAL billable
  // customers are included (includeInBilling from the backend: excludes
  // billing-exempt, unpriced/test-priced, internal Addy, and test orgs). The
  // on-screen table still shows everything; this only affects the download.
  const handleExport = async () => {
    const exportableOrgs = sortedOrgs.filter((org) => org.includeInBilling);
    if (!exportableOrgs.length) {
      toast.error("No billable organizations to export");
      return;
    }
    setExporting(true);
    try {
      const details = await mapWithConcurrency(
        exportableOrgs,
        6,
        async (org) => {
          try {
            const json = await fetchBillingUsageDetail(
              org.organizationId,
              { monthAndYear: period.monthAndYear },
              env,
            );
            const loans = (Array.isArray(json) ? json : []).flatMap(
              (bucket) => bucket?.loans || [],
            );
            return { organizationId: org.organizationId, loans, error: null };
          } catch (err) {
            return {
              organizationId: org.organizationId,
              loans: [],
              error: err?.message || "Failed to load loans",
            };
          }
        },
      );

      const detailByOrgId = new Map(
        details.map((detail) => [detail.organizationId, detail]),
      );

      const sections = [];
      const grand = { billableLoans: 0, totalCharge: 0 };
      exportableOrgs.forEach((org) => {
        const detail = detailByOrgId.get(org.organizationId);
        sections.push({ org, loans: detail?.loans || [] });
        grand.billableLoans += org.billableLoans || 0;
        grand.totalCharge += org.totalCharge || 0;
      });

      const workbook = buildBillingWorkbook(sections, grand);
      await downloadWorkbook(
        `enterprise-billing-usage-${period.monthAndYear}.xlsx`,
        workbook,
      );
    } catch (err) {
      toast.error(err?.message || "Failed to export billing usage");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="ebu-page">
      <ToastContainer position="top-right" autoClose={4000} />

      <header className="ebu-hero">
        <div className="ebu-hero-copy">
          <div className="ebu-eyebrow">
            <FaFileInvoiceDollar />
            Enterprise billing
          </div>
          <h1 className="ebu-title">Enterprise Billing Usage</h1>
          <p className="ebu-subtitle">
            Current-month per-loan billing usage across enterprise organizations.
          </p>
        </div>
        <div className="ebu-actions">
          <EnvToggle env={env} onChange={setEnv} />
          <PeriodSelects period={period} />
          <button
            type="button"
            className="ebu-btn ebu-btn--solid"
            onClick={() => load({ force: true })}
            disabled={loading}
          >
            <FaSync className={loading ? "fa-spin" : ""} />
            Refresh
          </button>
        </div>
      </header>

      <div className="ou-kpi-grid">
        <KpiCard
          label="Enterprise Orgs"
          value={fmtN(totals.enterpriseOrgs)}
          icon={<FaBuilding />}
          accent="indigo"
          subtext={monthLabel(data?.month || period.monthAndYear)}
        />
        <KpiCard
          label="Billable Loans"
          value={fmtN(totals.billableLoans)}
          icon={<FaList />}
          accent="green"
          subtext="Closed-stage billable volume"
        />
        <KpiCard
          label="Total Loans"
          value={fmtN(totals.totalLoans)}
          icon={<FaChartBar />}
          accent="cyan"
          subtext={`${fmtN(totals.nonBillableLoans)} non-billable`}
        />
        <KpiCard
          label="Projected Cost"
          value={fmtMoney(totals.totalCharge)}
          icon={<FaDollarSign />}
          accent="pink"
          subtext="Based on configured per-loan rates"
        />
        <KpiCard
          label="Potential Billable"
          value={fmtN(totals.potentialBillableLoans)}
          icon={<FaStream />}
          accent="amber"
          subtext="In-flight loans w/ checklist done (as of today)"
        />
        <KpiCard
          label="Dead Loans"
          value={fmtN(totals.deadLoans)}
          icon={<FaBan />}
          accent="rose"
          subtext={`Lost in ${monthLabel(data?.month || period.monthAndYear)}`}
        />
      </div>

      <div className="ebu-filterbar">
        <div className="ebu-filter-title">
          <span>
            <FaBuilding />
          </span>
          <div>
            <strong>Enterprise organizations</strong>
            <small>
              {loading ? "Refreshing" : `${fmtN(orgs.length)} results loaded`}
            </small>
          </div>
        </div>
        <div className="ebu-search">
          <FaSearch />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by organization name or ID"
          />
        </div>
        <button
          type="button"
          className="ebu-btn"
          onClick={handleExport}
          disabled={exporting || loading}
        >
          {exporting ? <FaSync className="fa-spin" /> : <FaDownload />}
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      {loading && !data ? (
        <LoadingPanel label="Loading enterprise billing usage..." />
      ) : !orgs.length ? (
        <EmptyPanel
          title="No enterprise billing usage found"
          subtitle="Try a different search term, period, or environment."
        />
      ) : (
        <div className="ebu-panel">
          <table className="ebu-table">
            <thead>
              <tr>
                <SortableHeader columnKey="name" sort={sort} setSort={setSort}>
                  Organization
                </SortableHeader>
                <SortableHeader columnKey="status" sort={sort} setSort={setSort}>
                  Status
                </SortableHeader>
                <th>Billable</th>
                <th>Total Loans</th>
                <th>Rate</th>
                <SortableHeader
                  columnKey="projectedCost"
                  sort={sort}
                  setSort={setSort}
                  align="right"
                >
                  Projected Cost
                </SortableHeader>
                <th>Mode</th>
                <th>Billing State</th>
              </tr>
            </thead>
            <tbody>
              {sortedOrgs.map((row) => (
                <tr
                  key={row.organizationId}
                  className="is-clickable"
                  onClick={() =>
                    history.push({
                      pathname: `/enterprise-billing-usage/${row.organizationId}`,
                      state: { organizationName: row.organizationName },
                    })
                  }
                >
                  <td className="ebu-org-cell">
                    <div className="ebu-org-name">{row.organizationName}</div>
                    <div className="ebu-org-id">{row.organizationId}</div>
                    {row.error ? (
                      <div className="ebu-org-id" title={row.error}>
                        {row.error}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <StatusBadge row={row} />
                  </td>
                  <td className="ebu-num">{fmtN(row.billableLoans)}</td>
                  <td className="ebu-num">
                    {fmtN(row.totalLoans)}
                    <span style={{ color: "var(--ou-muted)" }}>
                      {" "}
                      / {fmtN(row.nonBillableLoans)} non
                    </span>
                  </td>
                  <td className="ebu-num">
                    {row.pricePerLoan == null ? "-" : fmtMoney(row.pricePerLoan)}
                  </td>
                  <td className="ebu-num ebu-amount">
                    {fmtMoney(row.totalCharge)}
                  </td>
                  <td>
                    <span className="ebu-badge ebu-badge--muted">
                      {row.billingMode || "-"}
                    </span>
                  </td>
                  <td>
                    <span className="ebu-badge ebu-badge--muted">
                      {row.billingStatus || "-"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const LOAN_TABLE_PAGE_SIZE = 25;

export const EnterpriseBillingUsageDetailPage = ({ onReady }) => {
  const { organizationId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [env, setEnv] = useEnv();
  const period = useBillingPeriod({ allowAllTime: true });
  const [loading, setLoading] = useState(true);
  const [usageData, setUsageData] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [showNonBillable, setShowNonBillable] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  // Loan id currently being saved via the billable-override dropdown, so we
  // can disable just that row's control and show a spinner while it persists.
  const [savingLoanId, setSavingLoanId] = useState(null);

  const organizationName =
    location.state?.organizationName || decodeURIComponent(organizationId);

  // Same `force` semantics as the overview page: explicit Refresh button
  // skips both frontend + backend caches; normal navigation auto-loads can
  // serve from cache.
  const load = useCallback(
    async ({ force = false } = {}) => {
      setLoading(true);
      setLoadError(null);
      try {
        const json = await fetchBillingUsageDetail(
          organizationId,
          { monthAndYear: period.monthAndYear },
          env,
          { refresh: force },
        );
        setUsageData(Array.isArray(json) ? json : []);
      } catch (err) {
        // Surface the underlying reason on-page; toasts disappear in seconds
        // and a "Billing not configured" 400 is real signal, not absence of
        // data. Keep the toast for the transient cue.
        const message =
          err?.message || "Failed to load organization billing usage";
        const isConfigError = /billing|configur/i.test(message);
        setLoadError({
          message,
          kind: isConfigError ? "config" : "fetch",
        });
        toast.error(message);
        setUsageData([]);
      } finally {
        setLoading(false);
        onReady?.();
      }
    },
    [env, onReady, organizationId, period.monthAndYear],
  );

  useEffect(() => {
    load();
  }, [load]);

  // The backend returns one element per month when the period is "all time"
  // (and a single element when scoped to a specific month). Aggregate into a
  // single rolled-up view so the KPIs and loan table reflect the full window
  // — the client-side dashboard has a long-standing bug where it only shows
  // the first month, and we explicitly don't want to copy that for the CFO.
  const currentData = useMemo(() => {
    if (!usageData.length) return null;
    if (usageData.length === 1) return usageData[0];

    const aggregateLoans = [];
    let totalLoans = 0;
    let billableLoans = 0;
    let nonBillableLoans = 0;
    let totalCharge = 0;
    let totalSavings = 0;

    usageData.forEach((bucket) => {
      const s = bucket?.summary || {};
      totalLoans += s.totalLoans || 0;
      billableLoans += s.billableLoans || 0;
      nonBillableLoans += s.nonBillableLoans || 0;
      totalCharge += s.totalCharge || 0;
      totalSavings += s.totalSavings || 0;
      if (Array.isArray(bucket?.loans)) aggregateLoans.push(...bucket.loans);
    });

    // The discount tier / rate is per-org config, identical across months,
    // so we lift it from the first bucket rather than try to "average" it.
    const reference = usageData[0]?.summary || {};

    return {
      month: "all-time",
      summary: {
        totalLoans,
        billableLoans,
        nonBillableLoans,
        baseRatePerLoan: reference.baseRatePerLoan,
        discountTier: reference.discountTier,
        totalCharge,
        totalSavings,
        currency: reference.currency || "USD",
      },
      loans: aggregateLoans,
    };
  }, [usageData]);
  const summary = currentData?.summary || null;
  const allLoans = useMemo(() => currentData?.loans || [], [currentData]);

  const displayLoans = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allLoans.filter((loan) => {
      if (!showNonBillable && !loan.isBillable) return false;
      if (!q) return true;
      return (
        String(loan.id || "").toLowerCase().includes(q) ||
        String(loan.borrowerName || "").toLowerCase().includes(q) ||
        String(loan.stageName || "").toLowerCase().includes(q) ||
        String(loan.fundedDate || "").toLowerCase().includes(q)
      );
    });
  }, [allLoans, search, showNonBillable]);

  const totalPages = Math.max(1, Math.ceil(displayLoans.length / LOAN_TABLE_PAGE_SIZE));
  // Keep the active page in range whenever filters / data reduce the result set.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // Reset to page 1 when search / billable toggle / period changes — pagination
  // surviving filter changes would land the CFO on an empty page.
  useEffect(() => {
    setPage(1);
  }, [search, showNonBillable, period.monthAndYear]);

  const pagedLoans = useMemo(() => {
    const start = (page - 1) * LOAN_TABLE_PAGE_SIZE;
    return displayLoans.slice(start, start + LOAN_TABLE_PAGE_SIZE);
  }, [displayLoans, page]);

  const openLoanInNewTab = useCallback(
    (loanId) => {
      if (!loanId) return;
      // Deep-link into the client app (NOT the admin dashboard host — that
      // route doesn't exist here). Format mirrors what other admin pages use
      // (GlobalSearch, OrganizationLoanSearch, ProcessingMonitoring, etc.) so
      // the loan opens directly on the processing checklist board.
      const base = env === "dev" ? ADDY_APP_URL_DEV : ADDY_APP_URL_PROD;
      const url = `${base}/loans?tab=board&loan=${encodeURIComponent(
        loanId,
      )}&tabValue=processingChecklist`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [env],
  );

  // Persist a per-loan billable override. `selectValue` is one of:
  //   "auto" → clear the override (revert to rules-computed value)
  //   "yes"  → force billable
  //   "no"   → force non-billable
  // After saving we force-reload the detail so the summary KPIs + Amount
  // column recompute from the backend (single source of truth) rather than
  // duplicating the billing math here.
  const handleOverrideChange = useCallback(
    async (loanId, selectValue) => {
      const isBillable =
        selectValue === "auto" ? null : selectValue === "yes";
      setSavingLoanId(loanId);
      try {
        await setLoanBillingOverride(
          organizationId,
          loanId,
          { isBillable },
          env,
        );
        toast.success("Billing state updated");
        await load({ force: true });
      } catch (err) {
        toast.error(err?.message || "Failed to update billing state");
      } finally {
        setSavingLoanId(null);
      }
    },
    [env, load, organizationId],
  );

  const chartData = useMemo(() => {
    const byDate = {};
    allLoans.forEach((loan) => {
      if (!loan.fundedDate) return;
      if (!byDate[loan.fundedDate]) {
        byDate[loan.fundedDate] = {
          date: loan.fundedDate,
          totalLoans: 0,
          billableLoans: 0,
        };
      }
      byDate[loan.fundedDate].totalLoans++;
      if (loan.isBillable) byDate[loan.fundedDate].billableLoans++;
    });
    return Object.values(byDate).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
  }, [allLoans]);

  const handleExport = () => {
    if (!displayLoans.length) {
      toast.error("No loan rows to export");
      return;
    }
    downloadCsv(
      `enterprise-billing-usage-${organizationId}-${period.monthAndYear}.csv`,
      [
        "loanId",
        "borrowerName",
        "stageName",
        "fundedDate",
        "checklistCompleted",
        "billable",
        "rateApplied",
        "charge",
      ],
      displayLoans.map((loan) => [
        loan.id,
        loan.borrowerName || "",
        loan.stageName || "",
        loan.fundedDate || "",
        loan.checklistCompleted ? "yes" : "no",
        loan.isBillable ? "yes" : "no",
        loan.rateApplied || 0,
        loan.charge || 0,
      ]),
    );
  };

  return (
    <div className="ebu-page">
      <ToastContainer position="top-right" autoClose={4000} />

      <header className="ebu-detail-header">
        <div className="ebu-hero-copy">
          <button
            type="button"
            className="ebu-btn"
            onClick={() => history.push("/enterprise-billing-usage")}
            style={{ marginBottom: 12 }}
          >
            <FaArrowLeft />
            Back
          </button>
          <div className="ebu-eyebrow">
            <FaFileInvoiceDollar />
            Billing usage detail
          </div>
          <h1 className="ebu-title">{organizationName}</h1>
          <p className="ebu-subtitle">{organizationId}</p>
        </div>
        <div className="ebu-actions">
          <EnvToggle env={env} onChange={setEnv} />
          <PeriodSelects period={period} />
          <button
            type="button"
            className="ebu-btn ebu-btn--solid"
            onClick={() => load({ force: true })}
            disabled={loading}
          >
            <FaSync className={loading ? "fa-spin" : ""} />
            Refresh
          </button>
        </div>
      </header>

      {loading && !summary ? (
        <LoadingPanel label="Loading billing usage..." />
      ) : loadError ? (
        <EmptyPanel
          title={
            loadError.kind === "config"
              ? "Billing not configured for this organization"
              : "Could not load billing usage"
          }
          subtitle={
            loadError.kind === "config" ? (
              <>
                <div>{loadError.message}</div>
                <div style={{ marginTop: 8, opacity: 0.85 }}>
                  Configure <code>billing.pricePerLoan</code>,{" "}
                  <code>billing.basePricePerLoan</code>, and{" "}
                  <code>billing.discountRate</code> on the organization
                  document to unlock per-loan usage tracking.
                </div>
              </>
            ) : (
              loadError.message
            )
          }
        />
      ) : !summary ? (
        <EmptyPanel
          title="No usage data found"
          subtitle="This organization may not have per-loan billing usage for the selected period."
        />
      ) : (
        <>
          <div className="ou-kpi-grid">
            <KpiCard
              label="Billable Loans"
              value={fmtN(summary.billableLoans)}
              icon={<FaList />}
              accent="green"
              subtext={monthLabel(currentData.month)}
            />
            <KpiCard
              label="Total Loans"
              value={fmtN(summary.totalLoans)}
              icon={<FaChartBar />}
              accent="cyan"
              subtext={`${fmtN(summary.nonBillableLoans)} non-billable`}
            />
            <KpiCard
              label="Price Per Loan"
              value={fmtMoney(summary.discountTier?.ratePerLoan)}
              icon={<FaDollarSign />}
              accent="indigo"
              subtext={
                summary.discountTier?.discountPercent
                  ? `${summary.discountTier.discountPercent}% discount`
                  : "Configured rate"
              }
            />
            <KpiCard
              label="Total Cost"
              value={fmtMoney(summary.totalCharge)}
              icon={<FaFileInvoiceDollar />}
              accent="pink"
              subtext={`${fmtMoney(summary.totalSavings)} savings`}
            />
            <KpiCard
              label="Potential Billable"
              value={fmtN(summary.potentialBillableLoans)}
              icon={<FaStream />}
              accent="amber"
              subtext="In-flight w/ checklist done (as of today)"
            />
            <KpiCard
              label="Dead Loans"
              value={fmtN(summary.deadLoans)}
              icon={<FaBan />}
              accent="rose"
              subtext={`Lost in ${monthLabel(currentData.month)}`}
            />
            {summary.discountTier?.discountPercent > 0 ? (
              <KpiCard
                label="Discount Applied"
                value={`${summary.discountTier.discountPercent}%`}
                icon={<FaDollarSign />}
                accent="amber"
                subtext={`${fmtMoney(summary.totalSavings)} saved this period`}
              />
            ) : null}
          </div>

          <div className="ebu-panel" style={{ marginBottom: 18 }}>
            <div className="ebu-detail-tools">
              <div>
                <h2>Daily Usage Overview</h2>
                <div className="ebu-empty-sub">Billable loans by funded date</div>
              </div>
            </div>
            <div className="ebu-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--ou-border)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: "var(--ou-muted)" }}
                    stroke="var(--ou-border-strong)"
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 12, fill: "var(--ou-muted)" }}
                    stroke="var(--ou-border-strong)"
                  />
                  <Tooltip />
                  <Bar dataKey="billableLoans" fill="#2563eb" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="ebu-panel">
            <div className="ebu-detail-tools">
              <div>
                <h2>Loan Details</h2>
                <div className="ebu-empty-sub">
                  Showing {fmtN(displayLoans.length)} of {fmtN(allLoans.length)} loans
                </div>
              </div>
              <div className="ebu-actions">
                <div className="ebu-search" style={{ minWidth: 220 }}>
                  <FaSearch />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search loans"
                  />
                </div>
                <label className="ebu-checkbox">
                  <input
                    type="checkbox"
                    checked={showNonBillable}
                    onChange={(e) => setShowNonBillable(e.target.checked)}
                  />
                  Show Non-Billable
                </label>
                <button type="button" className="ebu-btn" onClick={handleExport}>
                  <FaDownload />
                  Export CSV
                </button>
              </div>
            </div>

            {displayLoans.length ? (
              <>
                <table className="ebu-table">
                  <thead>
                    <tr>
                      <th>Borrower</th>
                      <th>Loan ID</th>
                      <th>Stage</th>
                      <th>Funded Date</th>
                      <th>Checklist</th>
                      <th>Billable</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedLoans.map((loan) => (
                      <tr
                        key={loan.id}
                        className="is-clickable"
                        onClick={() => openLoanInNewTab(loan.id)}
                        title="Open loan in new tab"
                      >
                        <td className="ebu-org-cell">
                          <div className="ebu-org-name">
                            {loan.borrowerName || "Unknown borrower"}
                          </div>
                        </td>
                        <td className="ebu-org-id">{loan.id}</td>
                        <td>{loan.stageName || "-"}</td>
                        <td className="ebu-num">{loan.fundedDate || "-"}</td>
                        <td>
                          <span
                            className={`ebu-badge ${
                              loan.checklistCompleted ? "ebu-badge--ok" : "ebu-badge--muted"
                            }`}
                          >
                            {loan.checklistCompleted ? "Complete" : "Not complete"}
                          </span>
                        </td>
                        <td
                          onClick={(e) => e.stopPropagation()}
                          title="Override whether this loan is billed"
                        >
                          <div className="ebu-billable-cell">
                            <select
                              className={`ebu-billable-select ${
                                loan.billingOverride
                                  ? "is-overridden"
                                  : loan.isBillable
                                    ? "is-yes"
                                    : "is-no"
                              }`}
                              value={
                                loan.billingOverride
                                  ? loan.billingOverride.isBillable
                                    ? "yes"
                                    : "no"
                                  : "auto"
                              }
                              disabled={savingLoanId === loan.id}
                              onChange={(e) =>
                                handleOverrideChange(loan.id, e.target.value)
                              }
                            >
                              <option value="auto">
                                Auto (
                                {loan.isBillableComputed ? "Yes" : "No"})
                              </option>
                              <option value="yes">Billable</option>
                              <option value="no">Not billable</option>
                            </select>
                            {savingLoanId === loan.id ? (
                              <FaSync className="fa-spin ebu-billable-spin" />
                            ) : loan.billingOverride ? (
                              <span
                                className="ebu-badge ebu-badge--warn ebu-billable-tag"
                                title={
                                  loan.billingOverride.setAt
                                    ? `Overridden ${loan.billingOverride.setAt}`
                                    : "Manually overridden"
                                }
                              >
                                Manual
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="ebu-num ebu-amount">
                          {fmtMoney(loan.charge)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {totalPages > 1 ? (
                  <div className="ebu-pagination">
                    <div className="ebu-pagination-info">
                      Page <strong>{page}</strong> of <strong>{totalPages}</strong>{" "}
                      <span className="ebu-pagination-dim">
                        ({fmtN(displayLoans.length)} loans)
                      </span>
                    </div>
                    <div className="ebu-pagination-controls">
                      <button
                        type="button"
                        className="ebu-btn"
                        onClick={() => setPage(1)}
                        disabled={page <= 1}
                      >
                        First
                      </button>
                      <button
                        type="button"
                        className="ebu-btn"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1}
                      >
                        Prev
                      </button>
                      <button
                        type="button"
                        className="ebu-btn"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                      >
                        Next
                      </button>
                      <button
                        type="button"
                        className="ebu-btn"
                        onClick={() => setPage(totalPages)}
                        disabled={page >= totalPages}
                      >
                        Last
                      </button>
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="ebu-empty">
                <div className="ebu-empty-icon">
                  <FaInbox />
                </div>
                <div className="ebu-empty-title">No loans match</div>
                <div className="ebu-empty-sub">
                  Try clearing search or enabling non-billable loans.
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EnterpriseBillingUsagePage;
